import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getIsAdmin } from "@/lib/admin.functions";
import { Loader2, ShieldAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Betreiber-Auswertung – SalonTurbo" },
      {
        name: "description",
        content: "Interne Auswertung: Salons, Coins, Umsatz und Boost-Statistiken von SalonTurbo.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Betreiber-Auswertung – SalonTurbo" },
      { property: "og:description", content: "Interne Kennzahlen von SalonTurbo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminLayout,
  errorComponent: () => (
    <Card className="rounded-3xl">
      <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
        <ShieldAlert className="size-5 text-primary" />
        Auswertung konnte nicht geladen werden.
      </CardContent>
    </Card>
  ),
  notFoundComponent: () => (
    <Card className="rounded-3xl">
      <CardContent className="p-6 text-sm text-muted-foreground">Seite nicht gefunden.</CardContent>
    </Card>
  ),
});

const tabs = [
  { to: "/admin", label: "Kennzahlen", exact: true },
  { to: "/admin/salons", label: "Salons", exact: false },
  { to: "/admin/system", label: "Support & Technik", exact: false },
];

function AdminLayout() {
  const navigate = useNavigate();
  const check = useServerFn(getIsAdmin);
  const { data, isLoading } = useQuery({ queryKey: ["is-admin"], queryFn: () => check() });

  useEffect(() => {
    if (!isLoading && data && !data.isAdmin) navigate({ to: "/dashboard" });
  }, [data, isLoading, navigate]);

  if (isLoading || !data?.isAdmin) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30 px-4 py-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Betreiber</p>
          <h1 className="mt-1 text-2xl font-light sm:text-3xl">Auswertung</h1>
        </div>

        <nav className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              activeOptions={{ exact: tab.exact }}
              className="rounded-2xl border bg-background px-4 py-2 text-sm transition-colors data-[status=active]:border-primary data-[status=active]:bg-primary data-[status=active]:text-primary-foreground"
            >
              {tab.label}
            </Link>
          ))}
        </nav>

        <Outlet />
      </div>
    </div>
  );
}
